import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Receipt, MagnifyingGlass, Spinner, ArrowsClockwise, PencilSimple, CaretLeft, CaretRight, ChartPieSlice,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const _fmt = (v) => _num(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const _datetime = (s) => {
  if (!s) return '—';
  const t = String(s).replace(' ', 'T');
  const d = new Date(t);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const _date = (s) => {
  if (!s) return '—';
  const t = String(s).replace(' ', 'T');
  const d = new Date(t);
  if (isNaN(d.getTime())) return String(s).slice(0, 10);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const ALLOWED_STATUSES = ['Paid', 'Pending'];
const PAGE_SIZE = 8;

export default function CashierPaymentOrdersPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [status, setStatus] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      if (q.trim()) params.set('q', q.trim());
      if (status && status !== 'all') params.set('status', status);
      const { data } = await axios.get(
        `${API_URL}/api/cashiers/payment-orders${params.toString() ? `?${params}` : ''}`,
        { headers: authHeaders() },
      );
      setRows(Array.isArray(data) ? data : []);
      setPage(1);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load payment orders'));
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [dateFrom, dateTo, q, status]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load(true);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="cashier-payment-orders-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0A2540] flex items-center justify-center">
            <Receipt size={22} weight="duotone" className="text-[#C9A35B]" />
          </div>
          <div>
            <p
              className="text-[10px] uppercase tracking-[0.2em] text-[#78716C] font-semibold"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
            >
              Cashiers · Payment Orders
            </p>
            <h1
              className="text-2xl font-bold text-[#1C1917] mt-0.5"
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
            >
              Payment Orders
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate('/cashiers/payment-orders/overview')}
            data-testid="payment-orders-overview-btn"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-[#0A2540] text-xs font-semibold uppercase tracking-wider text-white hover:bg-[#0A2540]/90 transition"
          >
            <ChartPieSlice size={14} weight="bold" className="text-[#C9A35B]" />
            Overview
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="payment-orders-refresh"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold uppercase tracking-wider text-[#0A2540] hover:bg-[#FCFAF6] transition"
          >
            {refreshing
              ? <Spinner size={14} className="animate-spin" />
              : <ArrowsClockwise size={14} weight="bold" />}
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4 flex flex-wrap items-end gap-3">
        <FieldGroup label="Search">
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Payer, mobile, control no, purpose…"
              className="h-9 pl-8 text-sm w-[280px]"
              data-testid="payment-orders-search"
            />
          </div>
        </FieldGroup>
        <FieldGroup label="Status">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="h-9 text-sm w-[160px]" data-testid="payment-orders-status">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
              <SelectItem value="Paid">Paid</SelectItem>
            </SelectContent>
          </Select>
        </FieldGroup>
        <FieldGroup label="From">
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-9 text-sm w-[160px]"
            data-testid="payment-orders-from"
          />
        </FieldGroup>
        <FieldGroup label="To">
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-9 text-sm w-[160px]"
            data-testid="payment-orders-to"
          />
        </FieldGroup>
      </div>

      {/* Table — Channel column removed; Payer no longer shows mobile; Status is editable */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FCFAF6] hover:bg-[#FCFAF6]">
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Control #</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Payer</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Purpose</TableHead>
                <TableHead className="text-right text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Amount</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Status</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold">Paid On</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12">
                  <Spinner size={20} className="animate-spin text-[#0A2540] inline" />
                </TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-12 text-sm text-[#78716C]">
                  No payment orders found for the current filter.
                </TableCell></TableRow>
              ) : pagedRows.map((r) => (
                <TableRow key={r.order_id} className="hover:bg-[#FCFAF6]/50" data-testid={`po-row-${r.order_id}`}>
                  <TableCell className="text-xs font-mono text-[#78716C]">{_date(r.float_date || r.order_date)}</TableCell>
                  <TableCell className="text-xs font-mono text-[#0A2540] font-semibold">{r.control_number || '—'}</TableCell>
                  <TableCell className="text-sm font-medium text-[#1C1917]">{r.payer_name || '—'}</TableCell>
                  <TableCell className="text-sm text-[#1C1917]">{r.purpose || '—'}</TableCell>
                  <TableCell className="text-right text-sm font-semibold text-[#1C1917]">
                    {_fmt(r.amount)} <span className="text-[10px] text-[#78716C] ml-1">{r.currency || ''}</span>
                  </TableCell>
                  <TableCell>
                    {/paid|settled|success/i.test(String(r.payment_status || '')) ? (
                      <StatusBadge value={r.payment_status} />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditRow(r)}
                        data-testid={`po-status-edit-${r.order_id}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[#E2E8E5] hover:border-[#0A2540] hover:bg-[#FCFAF6] px-1.5 py-1 transition"
                        title="Update payment status"
                      >
                        <StatusBadge value={r.payment_status} />
                        <PencilSimple size={12} weight="bold" className="text-[#0A2540]" />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-[#78716C]">{_datetime(r.payment_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && rows.length > 0 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-[#EDE6D8] bg-[#FCFAF6]/60" data-testid="po-pagination">
            <span className="text-xs text-[#78716C]" data-testid="po-pagination-info">
              Showing <span className="font-semibold text-[#1C1917]">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, rows.length)}</span> of{' '}
              <span className="font-semibold text-[#1C1917]">{rows.length}</span> orders
            </span>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                data-testid="po-pagination-prev"
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <CaretLeft size={12} weight="bold" /> Prev
              </button>
              <PageNumbers current={safePage} total={totalPages} onSelect={setPage} />
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                data-testid="po-pagination-next"
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold text-[#0A2540] hover:bg-[#FCFAF6] transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next <CaretRight size={12} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>

      <UpdateStatusDialog
        row={editRow}
        onClose={() => setEditRow(null)}
      />
    </div>
  );
}

function PageNumbers({ current, total, onSelect }) {
  const pages = [];
  const start = Math.max(1, Math.min(current - 2, total - 4));
  const end = Math.min(total, start + 4);
  for (let i = start; i <= end; i += 1) pages.push(i);
  return (
    <div className="flex items-center gap-1">
      {start > 1 && <span className="text-xs text-[#78716C] px-1">…</span>}
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onSelect(p)}
          data-testid={`po-pagination-page-${p}`}
          className={`h-8 min-w-8 px-2 rounded-lg text-xs font-semibold transition ${
            p === current
              ? 'bg-[#0A2540] text-white'
              : 'border border-[#EDE6D8] bg-white text-[#0A2540] hover:bg-[#FCFAF6]'
          }`}
        >
          {p}
        </button>
      ))}
      {end < total && <span className="text-xs text-[#78716C] px-1">…</span>}
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div className="space-y-1">
      <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">{label}</span>
      {children}
    </div>
  );
}

function StatusBadge({ value }) {
  const v = String(value || '').trim() || 'Pending';
  const isPaid = /paid|settled|success/i.test(v);
  const isPending = /pend/i.test(v);
  const bg = isPaid ? '#E7F5EC' : isPending ? '#FEF3C7' : '#FBE7E7';
  const fg = isPaid ? '#047857' : isPending ? '#A16207' : '#B91C1C';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: bg, color: fg }}
    >
      {v}
    </span>
  );
}

/* ─── Update-status dialog (Save temporarily hidden — middleware endpoint pending) ── */
function UpdateStatusDialog({ row, onClose }) {
  const open = !!row;
  const [next, setNext] = useState('Pending');

  // Initialise the dropdown whenever the dialog opens with a new row.
  useEffect(() => {
    if (row) {
      const cur = String(row.payment_status || '').trim();
      setNext(ALLOWED_STATUSES.includes(cur) ? cur : 'Pending');
    }
  }, [row]);

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="po-update-status-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Update Payment Status
          </DialogTitle>
          <DialogDescription className="text-xs">
            Control&nbsp;# <span className="font-mono text-[#0A2540]">{row.control_number || row.order_id}</span> · {row.payer_name || '—'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-3 pt-1">
          <Meta label="Amount" value={`${_fmt(row.amount)} ${row.currency || ''}`.trim()} strong />
          <Meta label="Purpose" value={row.purpose} />
          <Meta label="Float Date" value={_date(row.float_date || row.order_date)} />
          <Meta label="Order Date" value={_datetime(row.order_date)} />
          <Meta label="Current Status">
            <StatusBadge value={row.payment_status} />
          </Meta>
        </div>

        <div className="border-t border-[#EDE6D8] my-2" />

        <div className="space-y-1.5">
          <span className="text-[10px] uppercase tracking-wider text-[#78716C] font-semibold block">New Status</span>
          <Select value={next} onValueChange={setNext} disabled>
            <SelectTrigger className="h-9 text-sm" data-testid="po-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ALLOWED_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`po-status-option-${s.toLowerCase()}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter className="pt-2">
          <Button type="button" variant="outline" onClick={onClose} data-testid="po-status-cancel">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Meta({ label, value, strong, children }) {
  return (
    <div className="min-w-0">
      <p
        className="text-[10px] uppercase tracking-widest text-[#78716C] font-semibold"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {label}
      </p>
      {children
        ? <div className="mt-1">{children}</div>
        : (
          <p className={`mt-0.5 text-sm text-[#1C1917] ${strong ? 'font-semibold' : ''} truncate`}>
            {value || '—'}
          </p>
        )
      }
    </div>
  );
}
